import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { Input, Radio, message } from 'antd';
import { IpcButton, IpcFormField, IpcModal, IpcStatusBadge, IpcTable } from '../components/ui';
import { nodeAutoCompleteRuleApi } from '../api/services';
import type { NodeAutoCompleteRuleForm } from '../types/nodeAutoCompleteRule';
import type {
  NodeAutoCompleteRuleNodeTestResultVO,
  NodeAutoCompleteRuleTestProjectVO,
  NodeAutoCompleteRuleTestResultVO,
  NodeAutoCompleteRuleStepTestResultVO,
} from '../api/types/nodeAutoCompleteRule';
import {
  DEFAULT_TEST_NODE_CONTEXT_JSON,
  DEFAULT_TEST_TRIGGER_PAYLOAD_JSON,
} from '../types/nodeAutoCompleteRuleScriptContext';
import './NodeAutoCompleteRuleTestModal.css';

const { TextArea } = Input;

type Props = {
  open: boolean;
  onClose: () => void;
  form: NodeAutoCompleteRuleForm;
};

function parseJsonInput(raw: string, label: string): unknown {
  const trimmed = raw.trim();
  if (!trimmed) return {};
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    throw new Error(`${label} 不是合法的 JSON`);
  }
}

function mapStep(vo?: NodeAutoCompleteRuleStepTestResultVO) {
  if (!vo) return null;
  return {
    success: vo.success,
    resultJson: vo.resultJson ?? vo.result_json,
    durationMs: vo.durationMs ?? vo.duration_ms ?? 0,
    logs: vo.logs ?? [],
    errorMessage: vo.errorMessage ?? vo.error_message,
  };
}

function renderStepResult(title: string, step: ReturnType<typeof mapStep>) {
  if (!step) return null;
  return (
    <section className="acr-test-result__step">
      <div className="acr-test-result__step-head">
        <span className="acr-test-result__step-title">{title}</span>
        {step.success ? (
          <IpcStatusBadge variant="success">成功 · {step.durationMs}ms</IpcStatusBadge>
        ) : (
          <IpcStatusBadge variant="destructive">失败 · {step.durationMs}ms</IpcStatusBadge>
        )}
      </div>
      {step.errorMessage ? (
        <pre className="acr-test-result__error">{step.errorMessage}</pre>
      ) : null}
      {step.resultJson ? (
        <pre className="acr-test-result__json">{step.resultJson}</pre>
      ) : null}
      {step.logs.length > 0 ? (
        <div className="acr-test-result__logs">
          <div className="acr-test-result__logs-title">console 输出</div>
          <pre className="acr-test-result__logs-body">{step.logs.join('\n')}</pre>
        </div>
      ) : null}
    </section>
  );
}

function nodeLabel(node: NodeAutoCompleteRuleNodeTestResultVO) {
  const spec = node.specName ?? node.spec_name ?? '规格';
  const milestone = node.milestoneName ?? node.milestone_name ?? '节点';
  const version = node.scheduleVersion ?? node.schedule_version;
  const scheduleId = node.scheduleId ?? node.schedule_id;
  const versionText = version != null ? `v${version}` : scheduleId != null ? `排期#${scheduleId}` : '';
  return [spec, milestone, versionText].filter(Boolean).join(' · ');
}

function renderNodeResult(node: NodeAutoCompleteRuleNodeTestResultVO, index: number) {
  const prepareStep = mapStep(node.prepare);
  const decisionStep = mapStep(node.decision);
  const err = node.errorMessage ?? node.error_message;
  const action = node.decisionAction ?? node.decision_action;
  return (
    <div key={`${node.specScheduleId ?? node.spec_schedule_id}-${node.milestoneId ?? node.milestone_id}-${index}`} className="acr-test-result__node">
      <div className="acr-test-result__node-head">
        <span className="acr-test-result__node-title">{nodeLabel(node)}</span>
        {node.success ? (
          <IpcStatusBadge variant="success">{action || '成功'}</IpcStatusBadge>
        ) : (
          <IpcStatusBadge variant="destructive">{action || '失败'}</IpcStatusBadge>
        )}
      </div>
      {err ? <pre className="acr-test-result__error">{err}</pre> : null}
      {renderStepResult('数据准备', prepareStep)}
      {renderStepResult('动作决策', decisionStep)}
    </div>
  );
}

function pick<T>(snake: T | undefined, camel: T | undefined): T | undefined {
  return camel ?? snake;
}

const NodeAutoCompleteRuleTestModal: FC<Props> = ({ open, onClose, form }) => {
  const [mode, setMode] = useState<'manual' | 'schedule'>('manual');
  const [triggerPayloadJson, setTriggerPayloadJson] = useState(DEFAULT_TEST_TRIGGER_PAYLOAD_JSON);
  const [nodeContextJson, setNodeContextJson] = useState(DEFAULT_TEST_NODE_CONTEXT_JSON);
  const [projectIds, setProjectIds] = useState<number[]>([]);
  const [projectKeyword, setProjectKeyword] = useState('');
  const [projectKeywordApplied, setProjectKeywordApplied] = useState('');
  const [projects, setProjects] = useState<NodeAutoCompleteRuleTestProjectVO[]>([]);
  const [projectsTotal, setProjectsTotal] = useState(0);
  const [projectPage, setProjectPage] = useState(1);
  const [projectPageSize, setProjectPageSize] = useState(10);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<NodeAutoCompleteRuleTestResultVO | null>(null);

  const ruleId = form.id ? Number(form.id) : 0;

  const loadProjects = useCallback(async () => {
    if (!ruleId) return;
    setProjectsLoading(true);
    try {
      const res = await nodeAutoCompleteRuleApi.getTestProjects(ruleId, {
        name: projectKeywordApplied.trim() || undefined,
        pageNum: projectPage,
        pageSize: projectPageSize,
      });
      setProjects(res.list ?? []);
      setProjectsTotal(res.total ?? 0);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '加载可选项目失败');
      setProjects([]);
      setProjectsTotal(0);
    } finally {
      setProjectsLoading(false);
    }
  }, [ruleId, projectKeywordApplied, projectPage, projectPageSize]);

  useEffect(() => {
    if (!open) return;
    setMode('manual');
    setTriggerPayloadJson(DEFAULT_TEST_TRIGGER_PAYLOAD_JSON);
    setNodeContextJson(DEFAULT_TEST_NODE_CONTEXT_JSON);
    setProjectIds([]);
    setProjectKeyword('');
    setProjectKeywordApplied('');
    setProjects([]);
    setProjectsTotal(0);
    setProjectPage(1);
    setResult(null);
  }, [open]);

  useEffect(() => {
    if (!open || mode !== 'schedule' || !ruleId) return;
    void loadProjects();
  }, [open, mode, ruleId, loadProjects]);

  const projectColumns = useMemo<ColumnsType<NodeAutoCompleteRuleTestProjectVO>>(() => [
    {
      title: '项目名称',
      key: 'name',
      width: 180,
      ellipsis: true,
      render: (_, row) => row.name || '—',
    },
    {
      title: '项目编码',
      key: 'code',
      width: 120,
      ellipsis: true,
      render: (_, row) => row.code || '—',
    },
    {
      title: '状态',
      key: 'status',
      width: 90,
      render: (_, row) => row.status || '—',
    },
    {
      title: '目标版本数',
      key: 'matching_schedule_count',
      width: 100,
      render: (_, row) => pick(row.matching_schedule_count, row.matchingScheduleCount) ?? 0,
    },
    {
      title: '目标版本',
      key: 'matching_schedule_labels',
      ellipsis: true,
      render: (_, row) => {
        const labels = pick(row.matching_schedule_labels, row.matchingScheduleLabels) ?? [];
        return labels.length > 0 ? labels.join('、') : '—';
      },
    },
  ], []);

  const handleRun = async () => {
    if (!ruleId) {
      message.warning('请先保存规则后再测试');
      return;
    }

    let triggerPayload: unknown;
    let nodeContext: unknown | undefined;
    try {
      triggerPayload = parseJsonInput(triggerPayloadJson, 'triggerPayload');
      if (mode === 'manual') {
        nodeContext = parseJsonInput(nodeContextJson, 'nodeContext');
      }
    } catch (e) {
      message.warning(e instanceof Error ? e.message : 'JSON 格式错误');
      return;
    }

    if (mode === 'schedule' && projectIds.length === 0) {
      message.warning('请选择至少一个项目');
      return;
    }

    setRunning(true);
    setResult(null);
    try {
      const res = await nodeAutoCompleteRuleApi.test({
        mode,
        ruleId,
        triggerPayload,
        nodeContext: mode === 'manual' ? nodeContext : undefined,
        projectIds: mode === 'schedule' ? projectIds : undefined,
      });
      setResult(res);
      if (res.success) {
        message.success('规则测试执行成功');
      } else {
        message.error(res.errorMessage ?? res.error_message ?? '规则测试执行失败');
      }
    } catch (e) {
      message.error(e instanceof Error ? e.message : '规则测试请求失败');
    } finally {
      setRunning(false);
    }
  };

  const prepareStep = mapStep(result?.prepare);
  const decisionStep = mapStep(result?.decision);
  const nodes = result?.nodes ?? [];
  const runId = result?.runId ?? result?.run_id;
  const runSource = result?.runSource ?? result?.run_source;
  const nodeTotal = result?.nodeTotal ?? result?.node_total;
  const nodeSuccess = result?.nodeSuccessCount ?? result?.node_success_count;
  const nodeFailed = result?.nodeFailedCount ?? result?.node_failed_count;
  const scheduleCount = result?.scheduleCount ?? result?.schedule_count;

  return (
    <IpcModal
      open={open}
      title={`测试规则${form.name.trim() ? ` · ${form.name.trim()}` : ''}`}
      width={920}
      onCancel={onClose}
      className="acr-rule-test-modal"
      footer={(
        <div className="acr-rule-test-modal__footer">
          <IpcButton variant="secondary" onClick={onClose}>关闭</IpcButton>
          <IpcButton variant="primary" loading={running} disabled={!ruleId} onClick={handleRun}>
            执行测试
          </IpcButton>
        </div>
      )}
    >
      <div className="acr-rule-test-modal__body">
        <Radio.Group
          value={mode}
          onChange={e => {
            setMode(e.target.value);
            setResult(null);
          }}
          options={[
            { value: 'manual', label: '手动输入入参' },
            { value: 'schedule', label: '选择项目（按目标版本执行）' },
          ]}
        />

        <IpcFormField label="triggerPayload">
          <TextArea
            className="acr-rule-test-modal__json"
            value={triggerPayloadJson}
            onChange={e => setTriggerPayloadJson(e.target.value)}
            rows={5}
            spellCheck={false}
          />
        </IpcFormField>

        {mode === 'manual' ? (
          <IpcFormField label="nodeContext">
            <TextArea
              className="acr-rule-test-modal__json"
              value={nodeContextJson}
              onChange={e => setNodeContextJson(e.target.value)}
              rows={12}
              spellCheck={false}
            />
          </IpcFormField>
        ) : (
          <IpcFormField label="选择项目">
            <div className="acr-rule-test-modal__projects">
              <div className="acr-rule-test-modal__projects-toolbar">
                <Input.Search
                  allowClear
                  placeholder="搜索项目名称/编码"
                  value={projectKeyword}
                  onChange={e => setProjectKeyword(e.target.value)}
                  onSearch={value => {
                    setProjectPage(1);
                    setProjectKeywordApplied(value);
                  }}
                  style={{ maxWidth: 280 }}
                />
              </div>
              <IpcTable<NodeAutoCompleteRuleTestProjectVO>
                frameless
                compact
                rowKey="id"
                loading={projectsLoading}
                columns={projectColumns}
                dataSource={projects}
                emptyTitle="暂无可选项目"
                emptyDescription="没有符合条件的目标版本项目"
                scroll={{ x: 700, y: 280 }}
                rowSelection={{
                  selectedRowKeys: projectIds,
                  preserveSelectedRowKeys: true,
                  onChange: keys => setProjectIds(keys.map(Number)),
                }}
                pagination={{
                  current: projectPage,
                  pageSize: projectPageSize,
                  total: projectsTotal,
                  pageSizeOptions: [10, 20, 50],
                  onChange: (page, size) => {
                    setProjectPage(page);
                    setProjectPageSize(size);
                  },
                }}
              />
            </div>
          </IpcFormField>
        )}

        {result ? (
          <div className="acr-test-result">
            <div className="acr-test-result__summary">
              {result.success ? (
                <IpcStatusBadge variant="success">整体成功</IpcStatusBadge>
              ) : (
                <IpcStatusBadge variant="destructive">整体失败</IpcStatusBadge>
              )}
              <span className="acr-test-result__meta">
                {[
                  runId != null ? `run_id=${runId}` : null,
                  runSource ? `run_source=${runSource}` : null,
                  scheduleCount != null ? `版本 ${scheduleCount}` : null,
                  nodeTotal != null ? `节点 ${nodeSuccess ?? 0}/${nodeTotal} 成功` : null,
                  nodeFailed != null && nodeFailed > 0 ? `失败 ${nodeFailed}` : null,
                ].filter(Boolean).join(' · ')}
              </span>
            </div>
            {(result.errorMessage ?? result.error_message) ? (
              <pre className="acr-test-result__error">{result.errorMessage ?? result.error_message}</pre>
            ) : null}
            {mode === 'manual' || prepareStep || decisionStep ? (
              <>
                {renderStepResult('第 2 步 · 数据准备', prepareStep)}
                {renderStepResult('第 3 步 · 动作决策', decisionStep)}
              </>
            ) : null}
            {nodes.length > 0 ? (
              <div className="acr-test-result__nodes">
                <div className="acr-test-result__nodes-title">节点执行明细</div>
                {nodes.map(renderNodeResult)}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </IpcModal>
  );
};

export default NodeAutoCompleteRuleTestModal;
